import type { ReactNode } from 'react';

/**
 * Bộ icon nét (stroke 1.9, khung 24) của khung Web Admin — chép từ bảng `I` của mockup v152 (sidebar) và các nút topbar.
 * Khóa trùng `NavItem.icon` trong routes.json; khóa lạ → chấm tròn (không viết cứng menu ở đây, chỉ cách VẼ biểu tượng).
 * Dựng bằng phần tử React (không innerHTML — CSP `script-src 'self'`, không `dangerouslySetInnerHTML`).
 */
const NAV_PATHS: Record<string, ReactNode> = {
  dash: <path d="M3 13h8V3H3zM13 21h8V3h-8zM3 21h8v-6H3z" />,
  product: (
    <>
      <path d="M21 8 12 3 3 8l9 5 9-5z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
    </>
  ),
  crm: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5a3 3 0 0 1 0 6" />
      <path d="M18 20a5 5 0 0 0-3-4.6" />
    </>
  ),
  price: (
    <>
      <path d="M20 12 12 4H4v8l8 8z" />
      <circle cx="8.5" cy="8.5" r="1.3" />
    </>
  ),
  credit: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  sales: (
    <>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2 3h3l2.5 12.5A2 2 0 0 0 9.5 17H18a2 2 0 0 0 2-1.6L21.5 8H6" />
    </>
  ),
  purchase: (
    <>
      <path d="M3 3h2l2 14h11l2-9H6" />
      <circle cx="9" cy="20" r="1.3" />
      <circle cx="18" cy="20" r="1.3" />
      <path d="M14 4h6M17 1v6" />
    </>
  ),
  inventory: (
    <>
      <path d="M3 7 12 2l9 5v10l-9 5-9-5z" />
      <path d="M3 7l9 5 9-5M12 12v10" />
    </>
  ),
  mfg: <path d="M3 21h18M4 21V9l5 3V9l5 3V4l5 4v13" />,
  payment: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 9h20M6 15h4" />
    </>
  ),
  logistics: (
    <>
      <path d="M1 6h13v11H1z" />
      <path d="M14 9h4l3 3v5h-7z" />
      <circle cx="5" cy="18" r="1.6" />
      <circle cx="18" cy="18" r="1.6" />
    </>
  ),
  oem: (
    <path d="M12 2a3 3 0 0 0-3 3v1H5a2 2 0 0 0-2 2v3a3 3 0 0 1 0 6v3a2 2 0 0 0 2 2h3v-1a3 3 0 0 1 6 0v1h3a2 2 0 0 0 2-2v-3a3 3 0 0 1 0-6V8a2 2 0 0 0-2-2h-4V5a3 3 0 0 0-3-3z" />
  ),
  social: <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-4-.9L3 21l1.9-4.9A8.4 8.4 0 1 1 21 11.5z" />,
  loyalty: <path d="m12 2 3 6.5 7 .6-5.3 4.6L18.5 21 12 17.3 5.5 21l1.8-7.3L2 9.1l7-.6z" />,
  aftersales: (
    <>
      <path d="M14 9a2 2 0 0 0-2-2H6l-4 4 4 4h6a2 2 0 0 0 2-2z" />
      <path d="M18 7a5 5 0 0 1 0 10" />
    </>
  ),
  dms: (
    <>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  notify: (
    <>
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  report: (
    <>
      <path d="M3 3v18h18" />
      <path d="M7 15l3-4 3 2 4-6" />
    </>
  ),
  admin: (
    <>
      <path d="M12 2 4 6v6c0 4.5 3.2 8.4 8 10 4.8-1.6 8-5.5 8-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  integration: (
    <>
      <path d="M8 8V5a3 3 0 0 1 6 0v3M6 8h12v11H6z" />
      <path d="M10 12h4" />
    </>
  ),
};

/** Icon nút topbar / điều khiển khung (mockup v152: ☰, nhà, kính lúp, ?, chuông, mũi tên). */
const UI_PATHS = {
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  home: (
    <>
      <path d="m3 11 9-8 9 8" />
      <path d="M5 10v10h14V10" />
      <path d="M9 20v-6h6v6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  chevronRight: <path d="m9 18 6-6-6-6" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2 4 6v6c0 4.5 3.2 8.4 8 10 4.8-1.6 8-5.5 8-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  device: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
} as const;

export type ShellIconName = keyof typeof UI_PATHS;

interface IconProps {
  size?: number | undefined;
  className?: string | undefined;
}

function Svg({ size = 18, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Icon mục menu theo `NavItem.icon`. */
export function NavIcon({ name, size, className }: IconProps & { name: string }) {
  return (
    <Svg size={size} className={className}>
      {NAV_PATHS[name] ?? <circle cx="12" cy="12" r="3" />}
    </Svg>
  );
}

/** Icon điều khiển khung (topbar, nhóm menu, menu tài khoản). */
export function ShellIcon({ name, size, className }: IconProps & { name: ShellIconName }) {
  return (
    <Svg size={size} className={className}>
      {UI_PATHS[name]}
    </Svg>
  );
}
